import { useState, useCallback, useEffect } from 'react';
import { fetchTechnicalAlertsFilters } from '../data/alertsApi';
import { useAuth } from '../context/AuthContext';

/**
 * Custom hook for managing dynamic technical alerts filters with faceted search
 * 
 * @param {Object} currentFilters - Current active filters (filteredInfo object)
 * @returns {Object} - { filterData, loading, error, refetchFilters }
 */
const useTechnicalAlertsFilters = (currentFilters = {}) => {
  const [filterData, setFilterData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  /**
   * Fetch filter values from the API
   * Supports faceted search by passing current filters
   * @param {Object} filters - Current filters to apply
   */
  const fetchFilters = useCallback(async (filters = {}) => {
    const clientId = currentUser?.customer_id;
    
    if (!clientId) {
      setFilterData(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetchTechnicalAlertsFilters(clientId, filters);
      setFilterData(result);
    } catch (err) {
      console.error('Error fetching technical alerts filters:', err);
      setError(err);
      // Set empty filter data on error to allow graceful degradation
      setFilterData({
        group: [],
        location: [],
        machineId: [],
        products: [], // Changed from productName, no more slots
        weeksSince: [],
        alertType: [],
        status: [],
        routes: []
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser?.customer_id]);

  // Fetch filters when clientId or filters change (faceted search)
  useEffect(() => {
    if (currentUser?.customer_id) {
      fetchFilters(currentFilters);
    }
  }, [currentFilters, fetchFilters, currentUser?.customer_id]);

  return {
    filterData,
    loading,
    error,
    refetchFilters: fetchFilters
  };
};

export default useTechnicalAlertsFilters;
